import { useRef, useMemo } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import * as THREE from 'three'

function Particles() {
  const pointsRef = useRef<THREE.Points>(null)

  const count = 2000
  
  const [positions, colors] = useMemo(() => {
    const positions = new Float32Array(count * 3)
    const colors = new Float32Array(count * 3)
    
    for (let i = 0; i < count; i++) {
      const i3 = i * 3
      positions[i3] = (Math.random() - 0.5) * 20
      positions[i3 + 1] = (Math.random() - 0.5) * 20
      positions[i3 + 2] = (Math.random() - 0.5) * 20
      
      const colorChoice = Math.random()
      if (colorChoice < 0.33) {
        colors[i3] = 0.37 // #5ed29c
        colors[i3 + 1] = 0.82
        colors[i3 + 2] = 0.61
      } else if (colorChoice < 0.66) {
        colors[i3] = 0.31 // #4fd8ff
        colors[i3 + 1] = 0.85
        colors[i3 + 2] = 1.0
      } else {
        colors[i3] = 0.55 // #8b5cf6
        colors[i3 + 1] = 0.36
        colors[i3 + 2] = 0.96
      }
    }
    
    return [positions, colors]
  }, [])

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y = state.clock.elapsedTime * 0.02
      pointsRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.1) * 0.1
    }
  })

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
          count={count}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          args={[colors, 3]}
          count={colors.length / 3}
          array={colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.05}
        vertexColors
        transparent
        opacity={0.8}
        sizeAttenuation
      />
    </points>
  )
}

function ConnectionLines() {
  const linesRef = useRef<THREE.LineSegments>(null)
  
  const connectionPositions = useMemo(() => {
    const positions = []
    const particleCount = 50
    
    for (let i = 0; i < particleCount; i++) {
      const x1 = (Math.random() - 0.5) * 10
      const y1 = (Math.random() - 0.5) * 10
      const z1 = (Math.random() - 0.5) * 10
      
      for (let j = i + 1; j < particleCount; j++) {
        const x2 = (Math.random() - 0.5) * 10
        const y2 = (Math.random() - 0.5) * 10
        const z2 = (Math.random() - 0.5) * 10
        
        const dist = Math.sqrt((x2-x1)**2 + (y2-y1)**2 + (z2-z1)**2)
        if (dist < 3) {
          positions.push(x1, y1, z1, x2, y2, z2)
        }
      }
    }
    
    return new Float32Array(positions)
  }, [])

  useFrame((state) => {
    if (linesRef.current) {
      linesRef.current.rotation.y = state.clock.elapsedTime * 0.01
    }
  })

  return (
    <lineSegments ref={linesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[connectionPositions, 3]}
          count={connectionPositions.length / 3}
          array={connectionPositions}
          itemSize={3}
        />
      </bufferGeometry>
      <lineBasicMaterial color="rgba(79, 216, 255, 0.2)" transparent opacity={0.2} />
    </lineSegments>
  )
}

export function ParticleUniverse() {
  return (
    <div className="absolute inset-0 -z-10">
      <Canvas camera={{ position: [0, 0, 8], fov: 60 }} dpr={[1, 1.5]}>
        <ambientLight intensity={0.1} />
        <Particles />
        <ConnectionLines />
      </Canvas>
    </div>
  )
}